// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- Reuse the existing workbench state until Chat V1 is retired.
import {useContentPreview} from '@github-ui/copilot-immersive-v1/components/ContentPreview/ContentPreviewContext'
import {useCallback, useLayoutEffect, useRef} from 'react'
import {announce} from '@github-ui/aria-live'

import {loadWorkbenchPreview, unavailablePreview, type WorkbenchPreviewSource} from './preview-source'

const activeRequests = new WeakMap<object, AbortController>()

export function cancelWorkbenchPreviewRequest(contentPreviewRef: object, disableLoadingState: () => void) {
  const request = activeRequests.get(contentPreviewRef)
  if (!request) return
  request.abort()
  activeRequests.delete(contentPreviewRef)
  disableLoadingState()
}

export function beginWorkbenchPreviewRequest(
  contentPreviewRef: object,
  disableLoadingState: () => void,
  selectionSignal?: AbortSignal,
) {
  cancelWorkbenchPreviewRequest(contentPreviewRef, disableLoadingState)
  const controller = new AbortController()
  activeRequests.set(contentPreviewRef, controller)
  const signal = selectionSignal ? AbortSignal.any([controller.signal, selectionSignal]) : controller.signal

  return {
    controller,
    signal,
    finish() {
      if (activeRequests.get(contentPreviewRef) !== controller) return false
      activeRequests.delete(contentPreviewRef)
      disableLoadingState()
      return true
    },
    cancel() {
      if (activeRequests.get(contentPreviewRef) !== controller) return
      controller.abort()
      activeRequests.delete(contentPreviewRef)
      disableLoadingState()
    },
  }
}

export function useWorkbenchPreviewSources() {
  const {
    updateItem,
    openEphemeralItem,
    openPreviewPane,
    enableLoadingState,
    disableLoadingState,
    contentPreviewRef,
    setResourcePanelRepoId,
    conversationScopeId,
    itemSelectionVersion,
  } = useContentPreview()
  const requestRef = useRef<ReturnType<typeof beginWorkbenchPreviewRequest> | undefined>(undefined)
  const cancelPendingPreview = useCallback(() => {
    activeRequests.get(contentPreviewRef)?.abort()
    activeRequests.delete(contentPreviewRef)
    disableLoadingState()
  }, [contentPreviewRef, disableLoadingState])
  useLayoutEffect(
    () => () => {
      requestRef.current?.cancel()
    },
    [conversationScopeId, itemSelectionVersion],
  )

  const openSource = useCallback(
    async (source: WorkbenchPreviewSource, returnFocusElement?: HTMLElement | null, selectionSignal?: AbortSignal) => {
      const request = beginWorkbenchPreviewRequest(contentPreviewRef, disableLoadingState, selectionSignal)
      requestRef.current = request
      openPreviewPane(returnFocusElement)
      enableLoadingState()
      let item: Awaited<ReturnType<typeof loadWorkbenchPreview>>
      try {
        item = await loadWorkbenchPreview(source, request.signal)
      } catch {
        item = unavailablePreview(source, 'Unable to load this file. Please try again.')
      }
      request.finish()
      if (request.signal.aborted) return false
      updateItem(item)
      openEphemeralItem(item.id)
      if (source.type === 'repository-file') {
        setResourcePanelRepoId({repoId: source.repo.id, ref: source.ref})
      }
      if ('unavailableReason' in item && item.unavailableReason) announce(item.unavailableReason)
      return !('unavailableReason' in item && item.unavailableReason)
    },
    [
      contentPreviewRef,
      disableLoadingState,
      enableLoadingState,
      openEphemeralItem,
      openPreviewPane,
      setResourcePanelRepoId,
      updateItem,
    ],
  )

  return {openSource, cancelPendingPreview}
}
